import { useCallback, useState } from 'react';
import config from '../config';
import {
  Error as ApiError,
  Job,
  JobOrderField,
  JobStatus,
  JobType,
  PaginatedRequest,
  PaginatedResponse,
} from '../types';
import pluckParams from '../utilities/pluck-params';
import prepareHeaders from '../utilities/prepare-headers';
import { useAuth } from './auth';

const useJobs = () => {
  const { token } = useAuth();

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleError = (error: Error) => {
    setPending(false);

    if (error.name === 'AbortError') {
      return;
    }

    setError(error);
  };

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const index = useCallback(
    async (
      options?: PaginatedRequest<JobOrderField> & {
        type?: JobType;
        status?: JobStatus[];
        listId?: string;
        indexId?: string;
        userId?: string;
      },
      signal?: AbortSignal
    ): Promise<PaginatedResponse<Job> | void> => {
      const actualOptions = Object.assign(
        {},
        {
          page: 1,
          limit: 10,
          order: 'createdAt',
          direction: 'desc',
        },
        options ?? {}
      );

      setPending(true);

      return fetch(
        `${config.api.url}/jobs?${new URLSearchParams({
          page: actualOptions.page.toString(),
          limit: actualOptions.limit.toString(),
          order: actualOptions.order,
          direction: actualOptions.direction,
          ...pluckParams(actualOptions, 'type', 'listId', 'indexId', 'userId'),
          ...(actualOptions.status?.length
            ? { status: actualOptions.status.join(',') }
            : {}),
        })}`,
        {
          method: 'GET',
          headers: prepareHeaders(token),
          signal,
        }
      )
        .then(async response => {
          setPending(false);
          setError(null);

          if (!response.ok) {
            setError(new Error(((await response.json()) as ApiError).message));
            return;
          }

          return (await response.json()) as PaginatedResponse<Job>;
        })
        .catch(handleError);
    },
    [token]
  );

  const show = useCallback(
    async (jobId: string, signal?: AbortSignal): Promise<Job | void> => {
      setPending(true);

      return fetch(`${config.api.url}/jobs/${jobId}`, {
        method: 'GET',
        headers: prepareHeaders(token),
        signal,
      })
        .then(async response => {
          setPending(false);
          setError(null);

          if (!response.ok) {
            setError(new Error(((await response.json()) as ApiError).message));
            return;
          }

          return (await response.json()) as Job;
        })
        .catch(handleError);
    },
    [token]
  );

  return {
    pending,
    error,
    resetError,
    index,
    show,
  };
};

export default useJobs;
